<template>
  <v-card
    elevation="5"
    class="info-card-base"
    :class="cardClasses"
    :style="cssVars"
  >
    <!-- Icon -->
    <div class="icon-wrapper">
      <v-icon
        class="icon-color"
        size="28"
      >
        {{ iconClass }}
      </v-icon>
    </div>

    <!-- Content -->
    <div class="card-content">
      <h4 class="card-title">
        {{ title }}
      </h4>

      <p
        v-if="viewDetail"
        class="card-desc"
        :title="description"
      >
        {{ description || '' }}
      </p>
    </div>
  </v-card>
</template>

<script>
export default {
  name: 'InfoCard',

  props: {
    title: {
      type: String,
      required: true
    },

    description: {
      type: String,
      default: ''
    },

    iconClass: {
      type: String,
      required: true
    },

    themeColor: {
      type: String,
      default: '#2196f3'
    },

    viewDetail: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    isRtl() {
      return !! ['fa', 'ar', 'ar_ae', 'ar_iq'].includes(this.$i18n.locale)
    },

    cardClasses() {
      return {
        'card-square': !this.viewDetail,
        'card-rect': this.viewDetail,
        'is-rtl': this.isRtl,
        'is-ltr': !this.isRtl
      }
    },
    cssVars() {
      let hex = String(this.themeColor || '#2196f3').replace('#', '')

      if (hex.length === 3) {
        hex = hex
          .split('')
          .map(char => char + char)
          .join('')
      }

      const r = parseInt(hex.substring(0, 2), 16) || 0
      const g = parseInt(hex.substring(2, 4), 16) || 0
      const b = parseInt(hex.substring(4, 6), 16) || 0

      return {
        '--theme-color': this.themeColor,
        '--theme-color-light': `rgba(${r}, ${g}, ${b}, 0.32)`,
        '--theme-color-light-hover': `rgba(${r}, ${g}, ${b}, 0.5)`
      }
    }
  }
}
</script>

<style scoped>
.info-card-base {
  position: relative;
  box-sizing: border-box;
  overflow: hidden;

  background-color: #ffffff;

  border-radius: 16px;

  cursor: pointer;

  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);

  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease,
    border-color 0.3s ease;
}

/* Hover */

.info-card-base:hover {
  transform: translateY(-2px);

  box-shadow: 0 6px 25px rgba(0, 0, 0, 0.08);
}


/* =========================================================
   Direction
   ========================================================= */

/*
 * جهت کارت از Vuetify می‌آید.
 *
 * RTL:
 * فارسی / عربی
 *
 * LTR:
 * انگلیسی و سایر زبان‌ها
 */

.info-card-base.is-rtl {
  direction: rtl;
}

.info-card-base.is-ltr {
  direction: ltr;
}


/* =========================================================
   Common Content
   ========================================================= */

.card-content {
  min-width: 0;
}

.card-title {
  margin: 0;

  font-weight: 700;

  color: #212121;

  overflow-wrap: anywhere;
  word-break: break-word;
}

.card-desc {
  margin: 0;

  color: #757575;

  overflow-wrap: anywhere;
  word-break: break-word;
   display: -webkit-box;
  -webkit-line-clamp: 2; /* تعداد خطوط مجاز */
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.icon-color {
  color: var(--theme-color);

  transition:
    color 0.3s ease,
    transform 0.3s ease;
}


/* =========================================================
   SQUARE CARD
   ========================================================= */

.card-square {
  display: flex;

  flex-direction: column;

  align-items: center;
  justify-content: center;

  width: 100%;

  min-width: 0;

  padding: 8px;

  /*
   * برای حفظ حالت مربعی
   */
  aspect-ratio: 1 / 1;

  text-align: center;

  border-top: 5px solid var(--theme-color-light);

  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease,
    border-color 0.3s ease;
}


/* Icon */

.card-square .icon-wrapper {
  display: flex;

  align-items: center;
  justify-content: center;

  width: 64px;
  height: 64px;

  flex-shrink: 0;

  margin-bottom: 16px;

  background-color: var(--theme-color-light);

  border-radius: 16px;

  transition:
    background-color 0.3s ease,
    transform 0.3s ease;
}


/* Title */

.card-square .card-title {
  max-width: 100%;

  margin-bottom: 12px;

  font-size: 1rem;

  line-height: 1.5;

  text-align: center;
}


/* Hover */

.card-square:hover {
  border-top-color: var(--theme-color);
}

.card-square:hover .icon-wrapper,
.card-square:active .icon-wrapper {
  background-color: var(--theme-color);

  transform: scale(1.02);
}

.card-square:hover .icon-color,
.card-square:active .icon-color {
  color: #e5e3e3;
}


/* =========================================================
   RECTANGULAR CARD
   ========================================================= */

.card-rect {
  display: flex;

  align-items: center;

  width: 100%;

  min-width: 0;
  min-height: 72px;

  padding: 15px 18px;

  /*
   * به صورت پیش‌فرض بدون border جانبی
   * و پایین‌تر بر اساس جهت تعیین می‌شود.
   */

  border-left: 0 solid transparent;
  border-right: 0 solid transparent;

  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease,
    border-color 0.3s ease;
}


/* =========================================================
   RTL RECTANGULAR
   ========================================================= */

.card-rect.is-rtl {
  text-align: right;

  border-right: 5px solid var(--theme-color-light-hover);
  border-left: 0;
}


/*
 * در RTL:
 *
 * [ متن ][ آیکون ]
 *
 * آیکون سمت چپ متن قرار می‌گیرد.
 */

.card-rect.is-rtl .icon-wrapper {
  order: 0;

  margin-left: 20px;
  margin-right: 0;
}


/* Content */

.card-rect.is-rtl .card-content {
  text-align: right;
}


/* =========================================================
   LTR RECTANGULAR
   ========================================================= */

.card-rect.is-ltr {
  text-align: left;

  border-left: 5px solid var(--theme-color-light-hover);
  border-right: 0;
}


/*
 * در LTR:
 *
 * [ آیکون ][ متن ]
 */

.card-rect.is-ltr .icon-wrapper {
  order: 0;

  margin-right: 20px;
  margin-left: 0;
}


/* Content */

.card-rect.is-ltr .card-content {
  text-align: left;
}


/* =========================================================
   Rect Icon
   ========================================================= */

.card-rect .icon-wrapper {
  display: flex;

  align-items: center;
  justify-content: center;

  width: 56px;
  height: 56px;

  flex: 0 0 56px;

  background-color: #ffffff;

  border-radius: 16%;

  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  transition:
    background-color 0.3s ease,
    transform 0.3s ease;
}


/* =========================================================
   Rect Content
   ========================================================= */

.card-rect .card-content {
  display: flex;

  flex: 1 1 auto;

  flex-direction: column;

  justify-content: center;

  min-width: 0;
}


/* Title */

.card-rect .card-title {
  margin-bottom: 6px;

  font-size: 1.1rem;

  line-height: 1.6;

  color: var(--theme-color);

  font-weight: 700;
}


/* Description */

.card-rect .card-desc {
  font-size: 0.85rem;

  line-height: 1.7;

  color: #757575;
  min-height: 3.4em; 

}


/* =========================================================
   Hover Rect
   ========================================================= */

.card-rect:hover .icon-wrapper {
  transform: translateY(-1px);
}


/* =========================================================
   Mobile
   ========================================================= */

@media (max-width: 599px) {
  .card-rect {
    min-height: 68px;

    padding: 12px 14px;
  }

  .card-rect .icon-wrapper {
    width: 48px;
    height: 48px;

    flex-basis: 48px;
  }

  .card-rect.is-rtl .icon-wrapper {
    margin-left: 14px;
  }

  .card-rect.is-ltr .icon-wrapper {
    margin-right: 14px;
  }

  .card-rect .card-title {
    font-size: 1rem;

    line-height: 1.5;
  }

  .card-rect .card-desc {
    font-size: 0.8rem;

    line-height: 1.6;
  }
}
</style>
